/**
 * V2DiscoverPage — Kindra V1 Discovery flow (/discover).
 *
 * A warm, plain-spoken conversational discovery experience. The client
 * experiences a friendly chat ("Kindra"); the framework underneath (5 leak
 * areas, coverage tracking, signal extraction) stays internal. No scores,
 * no leak names, no analysis are ever shown to the client in this phase.
 *
 * Access:
 *   • Behind the feature flag REACT_APP_DISCOVER_ENABLED (defaults true).
 *   • Not linked from public nav — live-but-unlisted at /discover.
 *
 * Session identity:
 *   • Anonymous to start (server-issued session_id stored in sessionStorage).
 *   • Email / business name captured naturally by Kindra mid-chat (LLM-side).
 *
 * Security:
 *   • LLM key (EMERGENT_LLM_KEY or ANTHROPIC_API_KEY) lives ONLY in the
 *     backend env. Browser hits /api/discover/* — no key exposure anywhere
 *     in this component or its network requests.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { V2 } from './V2Layout';
import V2Logo from './V2Logo';
import V2DiscoverWelcomeBack from './V2DiscoverWelcomeBack';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const ENABLED = String(process.env.REACT_APP_DISCOVER_ENABLED || 'true').toLowerCase() !== 'false';
const SESSION_KEY = 'kindra_discover_session_id';

// ─── Tiny inline icons (spectrum-coloured) ────────────────────────────────
const IconKindra = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
    <defs>
      <linearGradient id="spc-k" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#34D1B0" />
        <stop offset="50%" stopColor="#9156C4" />
        <stop offset="100%" stopColor="#EB258D" />
      </linearGradient>
    </defs>
    <circle cx="16" cy="16" r="14" fill="url(#spc-k)" />
    <path d="M11 9v14m0-7l8-7m-8 7l8 7" stroke="#fff" strokeWidth="2.2"
          strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

const IconSend = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M3 12l18-9-7 18-2-7-9-2z" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ─── Helpers ──────────────────────────────────────────────────────────────
function useReducedMotion() {
  const [r, setR] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setR(mq.matches);
    on();
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return r;
}

// ─── Sub-components ───────────────────────────────────────────────────────
const ChatHeader = () => (
  <header
    data-testid="discover-header"
    style={{
      background: V2.navy,
      color: V2.icy,
      borderBottom: `1px solid rgba(220,240,245,0.08)`,
    }}
    className="sticky top-0 z-30"
  >
    <div className="max-w-[760px] mx-auto px-5 py-3 flex items-center justify-between">
      <Link to="/" className="flex items-center" data-testid="discover-logo">
        <V2Logo variant="main" tone="icy" height={28} />
      </Link>
      <div className="flex items-center gap-2" style={{ color: 'rgba(220,240,245,0.6)' }}>
        <span className="text-[12px] tracking-[0.18em] uppercase font-semibold"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
          A quick chat
        </span>
      </div>
    </div>
  </header>
);

const Bubble = ({ role, text }) => {
  const isUser = role === 'user';
  return (
    <div
      data-testid={isUser ? 'discover-bubble-user' : 'discover-bubble-kindra'}
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}
    >
      {!isUser && (
        <div className="shrink-0 mr-2 mt-[2px]"><IconKindra size={28} /></div>
      )}
      <div
        className="rounded-[18px] px-4 py-3"
        style={{
          maxWidth: '82%',
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 16,
          lineHeight: 1.55,
          background: isUser ? V2.navy : '#fff',
          color: isUser ? V2.icy : V2.navy,
          border: isUser ? 'none' : `1px solid ${V2.hair}`,
          boxShadow: isUser ? 'none' : '0 1px 0 rgba(16,8,56,.04)',
          borderBottomRightRadius: isUser ? 6 : 18,
          borderBottomLeftRadius: isUser ? 18 : 6,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {text}
      </div>
    </div>
  );
};

const TypingDots = () => {
  const reduced = useReducedMotion();
  const dot = (delay) => (
    <span
      style={{
        width: 8, height: 8, borderRadius: '50%',
        background: 'rgba(16,8,56,0.35)',
        display: 'inline-block',
        animation: reduced ? 'none' : `kdotBounce 1.1s ease-in-out ${delay}ms infinite`,
      }}
    />
  );
  return (
    <div data-testid="discover-typing" className="flex justify-start mb-3">
      <div className="shrink-0 mr-2 mt-[2px]"><IconKindra size={28} /></div>
      <div
        className="rounded-[18px] px-4 py-3 flex items-center gap-1.5"
        style={{
          background: '#fff',
          border: `1px solid ${V2.hair}`,
          borderBottomLeftRadius: 6,
        }}
        aria-label="Kindra is typing"
      >
        {dot(0)}{dot(150)}{dot(300)}
      </div>
      <style>{`
        @keyframes kdotBounce {
          0%,80%,100% { transform: translateY(0); opacity: .35; }
          40%         { transform: translateY(-4px); opacity: .9;  }
        }
      `}</style>
    </div>
  );
};

const Composer = ({ value, onChange, onSend, sending, disabled }) => {
  const ref = useRef(null);
  // Auto-grow textarea — newer mobile keyboards push it up cleanly.
  useEffect(() => {
    const el = ref.current; if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 160) + 'px';
  }, [value]);
  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!disabled && value.trim()) onSend();
    }
  };
  return (
    <div
      className="sticky bottom-0 z-20"
      style={{ background: V2.warm, borderTop: `1px solid ${V2.hair}` }}
      data-testid="discover-composer"
    >
      <div className="max-w-[760px] mx-auto px-3 py-3 flex items-end gap-2">
        <textarea
          ref={ref}
          data-testid="discover-input"
          aria-label="Your reply"
          rows={1}
          placeholder={disabled && !sending ? '…' : 'Type your reply…'}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKey}
          className="flex-1 resize-none outline-none"
          style={{
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSize: 16,
            lineHeight: 1.5,
            color: V2.navy,
            background: '#fff',
            border: `1px solid ${V2.hair}`,
            borderRadius: 16,
            padding: '12px 14px',
            minHeight: 48,
            maxHeight: 160,
          }}
        />
        <button
          data-testid="discover-send"
          onClick={onSend}
          disabled={disabled || sending || !value.trim()}
          aria-label="Send"
          className="shrink-0 flex items-center justify-center transition-opacity"
          style={{
            width: 48, height: 48, borderRadius: 16,
            background: V2.spectrum,
            color: '#fff',
            opacity: disabled || sending || !value.trim() ? 0.5 : 1,
            cursor: disabled || sending || !value.trim() ? 'not-allowed' : 'pointer',
            border: 'none',
          }}
        >
          {sending
            ? <span style={{
                width: 16, height: 16, border: '2px solid #fff',
                borderTopColor: 'transparent', borderRadius: '50%',
                animation: 'kspin 0.9s linear infinite',
              }} />
            : <IconSend />}
        </button>
        <style>{`@keyframes kspin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
};

const CompleteScreen = () => (
  <div
    data-testid="discover-complete"
    className="max-w-[760px] mx-auto px-5 py-12 text-center"
  >
    <div className="inline-flex mb-6"><IconKindra size={52} /></div>
    <h1
      style={{
        fontFamily: 'Poppins, system-ui, sans-serif',
        fontWeight: 600,
        color: V2.navy,
        fontSize: 'clamp(28px, 5vw, 40px)',
        lineHeight: 1.15,
        letterSpacing: '-0.01em',
      }}
    >
      Beauty &mdash; that&apos;s everything I needed.
    </h1>
    <p
      className="mt-4 max-w-[520px] mx-auto"
      style={{
        fontFamily: 'Inter, system-ui, sans-serif',
        color: V2.muted,
        fontSize: 17,
        lineHeight: 1.6,
      }}
    >
      Michael will take a look before your call and come ready with where you might be quietly leaking time or money. Cheers for taking the time.
    </p>
    <div className="mt-8 inline-flex flex-col items-center gap-3">
      <Link
        to="/"
        data-testid="discover-home-link"
        className="inline-block"
        style={{
          fontFamily: 'Inter, system-ui, sans-serif',
          color: V2.navy,
          textDecoration: 'underline',
          textUnderlineOffset: 4,
          fontSize: 15,
        }}
      >
        Back to kindraai.dev
      </Link>
    </div>
    {/* Internal-only marker for QA — invisible to client. */}
    <span data-testid="discover-state-complete" hidden />
  </div>
);

// ─── Main page ────────────────────────────────────────────────────────────
export default function V2DiscoverPage() {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]); // [{role,content}]
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [starting, setStarting] = useState(true);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState('');
  // Welcome-Back gate state. When a tokened in_progress session resumes,
  // we keep the chat hidden until the client taps the CTA. Completed
  // sessions skip this entirely.
  const [welcomeBack, setWelcomeBack] = useState(null); // null | {firstName}
  const scrollRef = useRef(null);
  const bottomAnchorRef = useRef(null);
  const lastSendAtRef = useRef(0);

  const flagOff = !ENABLED;

  // Start session on mount (resume if we have one in sessionStorage).
  useEffect(() => {
    if (flagOff) { setStarting(false); return; }
    let cancelled = false;
    const start = async () => {
      try {
        // Pick up the booking-bound discovery_token from the URL (`?t=...`).
        // If present, /api/discover/start binds the new session to the booking's
        // HubSpot contact/deal/booking_id from the very first turn. If missing
        // or invalid, the server falls back to anonymous mode silently.
        const params = new URLSearchParams(window.location.search);
        const tokenFromUrl = (params.get('t') || '').trim();
        const startBody = tokenFromUrl ? { t: tokenFromUrl } : {};
        const r = await fetch(`${API}/discover/start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(startBody),
        });
        if (!r.ok) {
          const detail = await r.text();
          if (!cancelled) setError(r.status === 503
            ? 'The chat is off right now. Pop back in a few minutes.'
            : `Couldn't start the chat (${r.status}).`);
          return;
        }
        const data = await r.json();
        if (cancelled) return;
        sessionStorage.setItem(SESSION_KEY, data.session_id);
        setSessionId(data.session_id);

        // ── Resume rehydrate path ─────────────────────────────────────
        // The backend now returns `transcript: [{role,content},…]` for
        // both `already_completed` and `resumed` cases. When present
        // we paint the whole conversation instead of just the latest
        // assistant turn — so the client doesn't see a phantom restart.
        const fullTranscript = Array.isArray(data.transcript) && data.transcript.length > 0
          ? data.transcript.filter(m => m && (m.role === 'user' || m.role === 'assistant'))
          : null;

        if (data.already_completed) {
          // Completed → straight to the warm thank-you screen. We seed
          // `messages` with the last assistant turn (matches existing
          // behaviour) — the full transcript is intentionally NOT shown
          // back to the client today (out of scope this pass).
          setMessages([{ role: 'assistant', content: data.assistant }]);
          setComplete(true);
        } else if (data.resumed && fullTranscript) {
          // In-progress resume: prime the chat with the full prior
          // conversation, then gate it behind the Welcome Back screen.
          // Tapping the CTA drops the gate and reveals the chat scrolled
          // to the latest Kindra question.
          setMessages(fullTranscript);
          setWelcomeBack({ firstName: data.first_name || '' });
        } else {
          setMessages([{ role: 'assistant', content: data.assistant }]);
        }
      } catch (e) {
        if (!cancelled) setError('Network hiccup — try refreshing the page.');
      } finally {
        if (!cancelled) setStarting(false);
      }
    };
    start();
    return () => { cancelled = true; };
  }, [flagOff]);

  // Auto-scroll to bottom on new message / typing indicator.
  // We use a bottom-anchor + scrollIntoView (more robust on mobile than
  // calling scrollTo on the container, which can mis-calculate when the
  // iOS address bar shifts the dynamic viewport). Wrap in rAF so it runs
  // AFTER React has committed the new message into the DOM and the
  // browser has laid it out — otherwise scrollHeight reflects the
  // pre-update height.
  useEffect(() => {
    let id;
    const doScroll = () => {
      const anchor = bottomAnchorRef.current;
      if (!anchor) return;
      try {
        anchor.scrollIntoView({ behavior: 'smooth', block: 'end' });
      } catch (_) {
        // Older browsers without smooth-scroll support — fall back to
        // instant + raw scrollTop on the container.
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      }
    };
    // Double-rAF: first frame commits, second frame ensures layout is
    // settled (especially when a long assistant message lands at once).
    id = requestAnimationFrame(() => {
      id = requestAnimationFrame(doScroll);
    });
    return () => cancelAnimationFrame(id);
  }, [messages.length, sending, complete]);

  const send = async () => {
    const text = input.trim();
    if (!text || !sessionId || sending) return;
    // Lightweight client-side debounce — burst rate-limit on the backend
    // accepts 6 within 5s; this prevents fast double-taps from racing
    // each other and ensures the previous send finishes ordering.
    const now = Date.now();
    const last = lastSendAtRef.current || 0;
    if (now - last < 400) return;
    lastSendAtRef.current = now;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setSending(true);
    setError('');
    try {
      const r = await fetch(`${API}/discover/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, text }),
      });
      if (!r.ok) {
        if (r.status === 409) { setComplete(true); return; }
        // Rate-limit (429) is a recoverable, "slow down" condition —
        // distinguish it from a real upstream failure so the client
        // doesn't think Kindra broke. Pop the just-sent bubble back
        // into the input so they can resend with one tap.
        if (r.status === 429) {
          setMessages((m) => m.slice(0, -1));
          setInput(text);
          setError("Hang on a sec — Kindra's catching up. Tap send again in a moment.");
          return;
        }
        setError('Something went wrong sending that. Try again in a sec.');
        return;
      }
      const data = await r.json();
      setMessages((m) => [...m, { role: 'assistant', content: data.assistant }]);
      if (data.complete) setComplete(true);
    } catch (e) {
      // Network failure — pop the bubble and restore the input.
      setMessages((m) => m.slice(0, -1));
      setInput(text);
      setError('Network hiccup — tap send again.');
    } finally {
      setSending(false);
    }
  };

  // ── Render branches ────────────────────────────────────────────────────
  if (flagOff) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5"
           style={{ background: V2.warm }} data-testid="discover-disabled">
        <p style={{ color: V2.muted, fontFamily: 'Inter, system-ui' }}>Not available.</p>
      </div>
    );
  }

  if (complete) {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: V2.warm }}>
        <ChatHeader />
        <main className="flex-1 flex items-center justify-center">
          <CompleteScreen />
        </main>
      </div>
    );
  }

  // Welcome Back gate — only shown when /discover/start resolved a
  // tokened in_progress session (resumed:true). Tapping the CTA drops
  // the gate and reveals the chat scrolled to the latest Kindra
  // question. Strict token isolation is enforced upstream in
  // /api/discover/start (booking_id defence-in-depth check).
  if (welcomeBack) {
    return (
      <V2DiscoverWelcomeBack
        firstName={welcomeBack.firstName}
        onContinue={() => setWelcomeBack(null)}
      />
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: V2.warm }}
         data-testid="discover-page">
      <ChatHeader />
      <main
        ref={scrollRef}
        className="flex-1 overflow-y-auto"
        style={{ paddingTop: 16 }}
        data-testid="discover-scroll"
      >
        <div className="max-w-[760px] mx-auto px-4 pb-10">
          {starting && (
            <div data-testid="discover-starting" className="py-12 text-center"
                 style={{ color: V2.muted, fontFamily: 'Inter, system-ui' }}>
              <div className="inline-flex mb-4"><IconKindra size={36} /></div>
              <div>Just a sec…</div>
            </div>
          )}
          {!starting && messages.map((m, i) => (
            <Bubble key={i} role={m.role} text={m.content} />
          ))}
          {sending && <TypingDots />}
          {error && (
            <div
              data-testid="discover-error"
              className="rounded-[12px] px-4 py-3 mb-3"
              style={{
                background: 'rgba(235,37,141,0.06)',
                border: '1px solid rgba(235,37,141,0.25)',
                color: '#9b1561',
                fontFamily: 'Inter, system-ui',
                fontSize: 14,
              }}
            >
              {error}
            </div>
          )}
          {/* Bottom anchor — sits below the last message so scrollIntoView
              lands the newest content squarely above the composer. The
              extra padding avoids the smooth-scroll resting right against
              the composer's top border. */}
          <div
            ref={bottomAnchorRef}
            data-testid="discover-scroll-anchor"
            aria-hidden
            style={{ height: 1, width: '100%' }}
          />
        </div>
      </main>
      <Composer
        value={input}
        onChange={setInput}
        onSend={send}
        sending={sending}
        disabled={starting || !sessionId}
      />
    </div>
  );
}
